import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Globe, MapPin, Filter, Search, ExternalLink,
  ArrowLeft, CheckCircle, Heart, Scale, Briefcase, HandHeart,
  X, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const focusIcons = {
  justice: Scale,
  labor: Briefcase,
  peace: Heart,
  mindfulness: Heart,
  community: Users,
  mutual_aid: HandHeart,
  faith: Building2
};

const focusLabels = {
  justice: 'Justice',
  labor: 'Workers Rights',
  peace: 'Peace',
  mindfulness: 'Mindfulness',
  community: 'Community',
  mutual_aid: 'Mutual Aid',
  faith: 'Faith',
  spirituality: 'Spirituality',
  entrepreneurship: 'Entrepreneurship',
  poverty_relief: 'Poverty Relief',
  children: 'Children',
  meditation: 'Meditation',
  conversation: 'Conversation',
  meaning: 'Meaning',
  solidarity: 'Solidarity',
  support: 'Support',
  growth: 'Growth',
  workers_rights: 'Workers Rights',
  politics: 'Politics'
};

export default function TribeFinder() {
  const [tribes, setTribes] = useState([]);
  const [filteredTribes, setFilteredTribes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    faithLanguage: null,
    secularFriendly: null
  });

  const fetchTribes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/faith/tribes`);
      if (res.ok) {
        const data = await res.json();
        setTribes(data.tribes);
        setFilteredTribes(data.tribes);
      }
    } catch (err) {
      console.error('Error fetching tribes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTribes();
  }, [fetchTribes]);

  useEffect(() => {
    let result = [...tribes];

    // Type filter
    if (filters.type !== 'all') {
      if (filters.type === 'online') {
        result = result.filter(t => t.type.includes('online'));
      } else if (filters.type === 'local') {
        result = result.filter(t => t.type.includes('local'));
      }
    }

    // Faith language filter
    if (filters.faithLanguage !== null) {
      result = result.filter(t => t.faith_language === filters.faithLanguage);
    }

    // Secular friendly filter
    if (filters.secularFriendly !== null) {
      result = result.filter(t => t.secular_friendly === filters.secularFriendly);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.focus.some(f => f.toLowerCase().includes(query))
      );
    }

    setFilteredTribes(result);
  }, [tribes, filters, searchQuery]);

  const clearFilters = () => {
    setFilters({ type: 'all', faithLanguage: null, secularFriendly: null });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.type !== 'all' || 
    filters.faithLanguage !== null || 
    filters.secularFriendly !== null || 
    searchQuery;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-emerald-50/30" data-testid="tribe-finder">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/faith" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Faith Hub
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <Users className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Community Discovery</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Find Your Tribe</h1>
              <p className="text-emerald-100 max-w-2xl">
                Discover communities that share your values—spiritual, secular, justice-focused. 
                We rise together or not at all.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search communities..."
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Type Tabs */}
              <Tabs value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="online">
                    <Globe className="h-4 w-4 mr-1" /> Online
                  </TabsTrigger>
                  <TabsTrigger value="local">
                    <MapPin className="h-4 w-4 mr-1" /> Local
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Toggle Filters */}
            <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t">
              <div className="flex items-center gap-3">
                <Switch
                  checked={filters.faithLanguage === true}
                  onCheckedChange={(c) => setFilters(f => ({ 
                    ...f, 
                    faithLanguage: c ? true : (f.faithLanguage === true ? null : f.faithLanguage)
                  }))}
                />
                <Label>Faith-Based Language</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={filters.secularFriendly === true}
                  onCheckedChange={(c) => setFilters(f => ({ 
                    ...f, 
                    secularFriendly: c ? true : (f.secularFriendly === true ? null : f.secularFriendly)
                  }))}
                />
                <Label>Secular-Friendly</Label>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto">
                  <X className="h-4 w-4 mr-1" /> Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-stone-600">
            {filteredTribes.length} {filteredTribes.length === 1 ? 'community' : 'communities'} found
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {filteredTribes.map((tribe, idx) => (
            <motion.div
              key={tribe.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{tribe.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {tribe.type.includes('online') && (
                          <Badge variant="outline" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" /> Online
                          </Badge>
                        )}
                        {tribe.type.includes('local') && (
                          <Badge variant="outline" className="text-xs">
                            <MapPin className="h-3 w-3 mr-1" /> Local
                          </Badge>
                        )}
                      </div>
                    </div>
                    {tribe.cost === 'free' && (
                      <Badge className="bg-green-100 text-green-700">Free</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-600 text-sm mb-4">{tribe.description}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {tribe.focus.slice(0, 4).map(f => (
                      <Badge key={f} variant="secondary" className="text-xs">
                        {focusLabels[f] || f}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex gap-2">
                      {tribe.faith_language && (
                        <span className="text-xs text-stone-500">Faith Language</span>
                      )}
                      {tribe.secular_friendly && (
                        <span className="text-xs text-stone-500">Secular Friendly</span>
                      )}
                    </div>
                    
                    <a
                      href={tribe.url.startsWith('/') ? tribe.url : tribe.url}
                      target={tribe.url.startsWith('/') ? '_self' : '_blank'}
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                        Explore <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {filteredTribes.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No communities found</h3>
              <p className="text-stone-500 mb-4">Try adjusting your filters or search terms.</p>
              <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Card className="mt-12 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="py-8 text-center">
            <h3 className="text-xl font-bold text-stone-800 mb-2">Can't Find Your Tribe?</h3>
            <p className="text-stone-600 mb-4">
              The DowUrk community is always here. Connect with fellow entrepreneurs and workers.
            </p>
            <Link to="/community">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Join DowUrk Community <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
